import { GetProviderResponseItem } from '@/api/api.schemas';
import styles from './provider-list-card.module.css';
import { SelectProviderButton } from '@/components/encounter/provider/select-provider-button';
import clsx from 'clsx';
import { formatDate } from '@/util/formats';
import { InfoRow, TextDivider } from '@/components/encounter/info-row';
import { Button } from '@/components/button';
import { supportEmail } from '@/util/urls';
import { ProviderImage } from './provider-image';
import { formatState } from '@/util/helpers';
import { useTranslation } from '@/contexts/i18n-context';

const translationBase = 'visit.providerSelection';

interface ProviderListCardProps {
  provider: GetProviderResponseItem;
  showSelectButton?: boolean;
  shortView?: boolean;
}

export function ProviderListCard({
  provider,
  showSelectButton = true,
  shortView = false,
}: ProviderListCardProps) {
  const { t } = useTranslation();

  const isProviderUnavailable = Boolean(provider.nextAvailableDate) || !provider.isActive;

  return (
    <div
      className={styles.card}
      data-testid="provider-card"
      data-provider-id={provider.id.toUpperCase()}
    >
      <div className={styles.detailsContainer}>
        <ProviderImage url={provider.img} className={styles.providerImg} />
        <div className={styles.providerDetails}>
          <h1 className={clsx('body-1', 'bold')}>{provider.fullName}</h1>
          {provider.officeState && (
            <InfoRow
              icon="pin_drop"
              title={t(`${translationBase}.providerCard.timeZone`)}
              details={
                <TextDivider
                  details={[provider.officeState.timeZone, formatState(provider.officeState)]}
                />
              }
            />
          )}
          {!!provider.residencyTraining && provider.isCertified && (
            <InfoRow
              icon="home_health"
              title={t(`${translationBase}.providerCard.residencyTraining`)}
              details={provider.residencyTraining}
            />
          )}
          {!!provider.medicalSchool &&
            (provider.isCertified ? (
              <InfoRow
                icon="school"
                title={t(`${translationBase}.providerCard.medicalSchool`)}
                details={provider.medicalSchool}
              />
            ) : (
              <InfoRow
                icon="home_health"
                title={t(`${translationBase}.providerCard.education`)}
                details={provider.medicalSchool}
              />
            ))}
          {!!provider.graduationYear && (
            <InfoRow
              icon="calendar_today"
              title={t(`${translationBase}.providerCard.graduationYear`)}
              details={provider.graduationYear.toString()}
            />
          )}
        </div>
      </div>
      {showSelectButton && (
        <SelectProviderButton
          className={styles.selectButton}
          provider={provider}
          disabled={isProviderUnavailable}
          variant="outlined"
          data-testid="provider-card-select"
          aria-label={`${t(`${translationBase}.selectProviderBtn`)} ${provider.fullName}`}
        >
          {t(`${translationBase}.selectProviderBtn`)}
        </SelectProviderButton>
      )}

      {!shortView && isProviderUnavailable && (
        <p className={clsx('body-4', styles.providerUnavailableText)}>
          {provider.isActive
            ? t(`${translationBase}.providerCard.outOfOffice`, {
                returnDate: formatDate(provider.nextAvailableDate, {
                  format: 'user',
                }),
              })
            : t(`${translationBase}.notAvailable`, {
                link: (
                  <Button
                    className={clsx(styles.supportLinkBtn, 'body-4')}
                    variant="link"
                    size="S"
                    as="a"
                    to={supportEmail}
                    key="support-email-link"
                  >
                    {t(`${translationBase}.bannerLink`)}
                  </Button>
                ),
              })}
        </p>
      )}
    </div>
  );
}
